import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Play, X, Clock, ArrowRight, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { VIDEO_SRC, VIDEO_POSTER } from "../mock";
import { track, makeVideoTracker } from "../lib/analytics";

const VideoSection = () => {
  const [open, setOpen] = useState(false);
  const [ended, setEnded] = useState(false);
  const videoRef = useRef(null);
  const vt = useRef(makeVideoTracker({ title: "Full Stack Explainer", id: "explainer" })).current;
  const navigate = useNavigate();
  const location = useLocation();

  const goMatrix = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    track("take_matrix_click", { location: "video_section" });
    if (location.pathname === "/matrix") {
      const el = document.getElementById("matrix");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/matrix");
    }
  };

  const openVideo = () => {
    track("watch_video_click", { location: "video_section" });
    setEnded(false);
    setOpen(true);
  };

  const takeMatrixFromVideo = () => {
    setOpen(false);
    setTimeout(goMatrix, 250);
  };

  const replay = async () => {
    setEnded(false);
    try {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        await videoRef.current.play();
      }
    } catch (err) {
      /* ignore */
    }
  };

  // Autoplay when the modal opens (within the click gesture)
  useEffect(() => {
    if (!open || !videoRef.current) return;
    const v = videoRef.current;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }, [open]);

  return (
    <section id="video" className="pt-3 md:pt-6 pb-12 md:pb-20">
      <div className="w-[90%] max-w-[980px] mx-auto text-center">
        <span className="inline-block text-[11px] font-bold tracking-[0.35em] uppercase text-primary mb-3 md:mb-4">
          Watch First
        </span>
        <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-5xl">
          <span className="text-gold-gradient block">Full Stack Coaching</span>
          <span className="block">See how it works</span>
        </h2>
        <p className="mt-3 md:mt-5 text-base md:text-lg text-white/70 font-medium max-w-2xl mx-auto">
          Four minutes with Sandra on the seven systems, and why treating them together is the
          difference between spinning your wheels and getting your body back.
        </p>
        {/* Poster with the Full Stack definition — gold "TV" frame */}
        <button
          onClick={openVideo}
          className="group relative block w-full mt-5 md:mt-8 rounded-[1.75rem] overflow-hidden border-[3px] border-primary/70 shadow-[0_0_0_1px_rgba(212,175,55,0.25),0_18px_60px_-15px_rgba(212,175,55,0.35)] focus-ring text-center"
          aria-label="Play the Full Stack Fitness video"
        >
          <img
            src={VIDEO_POSTER}
            alt="Sandra explaining the seven systems of Full Stack Fitness"
            className="absolute inset-0 w-full h-full object-cover opacity-[0.14] group-hover:opacity-20 transition duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />

          <div className="relative z-10 flex flex-col items-center justify-center px-5 md:px-12 pt-6 pb-14 md:py-0 md:aspect-video">
            <div className="flex items-baseline gap-2 justify-center">
              <span className="font-mono font-bold uppercase tracking-[0.2em] text-primary text-sm md:text-xl">
                Full&nbsp;Stack
              </span>
              <span className="font-mono text-white/35 text-[10px] md:text-xs">/ noun /</span>
            </div>
            <div className="mt-1.5 md:mt-2 h-px w-40 md:w-48 bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
            <p className="mt-2.5 md:mt-3 font-mono text-[10px] md:text-[13px] text-white leading-snug md:leading-relaxed max-w-lg">
              In software, a full-stack developer builds both halves of the app: the front end you
              see, and the back end that powers it.
            </p>
            <p className="mt-2.5 md:mt-3 text-sm md:text-xl font-black uppercase tracking-tight text-white leading-tight font-display">
              A woman runs on the same architecture.
            </p>
            <p className="mt-2 font-mono text-[10px] md:text-[13px] text-white leading-snug md:leading-relaxed max-w-lg">
              Front end: the outside. Back end: everything underneath that runs{"\u00a0"}it.
            </p>
            <p className="mt-2.5 md:mt-3 text-base md:text-2xl font-black uppercase tracking-tight text-gold-gradient leading-tight font-display">
              We don't train half of you.
            </p>

            <span className="mt-4 md:mt-6 relative flex items-center justify-center w-14 h-14 md:w-20 md:h-20 rounded-full bg-primary text-primary-foreground shadow-[0_12px_40px_-6px_rgba(212,175,55,0.7)] group-hover:scale-110 transition-transform duration-300">
              <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping" />
              <Play size={26} className="relative ml-1 fill-current" />
            </span>
            <span className="mt-2.5 md:mt-3 text-[11px] md:text-[12px] font-bold tracking-[0.28em] uppercase text-white">
              Watch Video Now
            </span>
          </div>

          <span className="absolute bottom-4 right-4 z-20 flex items-center gap-1.5 rounded-full bg-black/70 backdrop-blur px-3 py-1.5 text-xs font-semibold text-white/90 border border-white/10">
            <Clock size={13} /> 4 min watch
          </span>
        </button>

        <p className="mt-8 text-white/60 font-medium">
          Once it clicks,{" "}
          <button onClick={goMatrix} className="text-primary font-bold hover:underline underline-offset-4">
            take the Matrix
          </button>{" "}
          and see which systems are yours.
        </p>
      </div>

      {/* Lightbox player */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 bg-black border-white/10 overflow-hidden">
          <DialogTitle className="sr-only">Full Stack Fitness video</DialogTitle>
          <DialogDescription className="sr-only">
            A four-minute video with Sandra on the seven systems of Full Stack Fitness.
          </DialogDescription>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close video"
            className="absolute top-3 right-3 z-20 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center border border-white/15 focus-ring"
          >
            <X size={20} />
          </button>

          <div className="relative w-full bg-black">
            {/* Keep 16:9 while the video is playing */}
            <div className={ended ? "hidden" : "aspect-video w-full"}>
              {open && (
                <video
                  ref={videoRef}
                  src={VIDEO_SRC}
                  poster={VIDEO_POSTER}
                  controls
                  autoPlay
                  playsInline
                  preload="auto"
                  className="w-full h-full bg-black"
                  onPlay={vt.onPlay}
                  onTimeUpdate={vt.onTimeUpdate}
                  onCanPlay={(e) => {
                    const p = e.currentTarget.play();
                    if (p && p.catch) p.catch(() => {});
                  }}
                  onEnded={(e) => {
                    vt.onEnded(e);
                    setEnded(true);
                  }}
                />
              )}
            </div>

            {/* End-of-video call to action — self-sizing so it never clips on mobile */}
            {ended && (
              <div className="w-full text-center px-5 sm:px-6 py-12 sm:py-16 flex flex-col items-center bg-black fade-up">
                <span className="text-[10px] sm:text-[11px] font-bold tracking-[0.35em] uppercase text-primary mb-3 sm:mb-4">
                  Your Turn
                </span>
                <h3 className="font-display font-black uppercase tracking-tighter leading-[1.0] text-2xl sm:text-3xl md:text-4xl text-white max-w-2xl">
                  Take the Menopause Matrix<sup className="align-super text-[0.55em] font-semibold">™</sup> now
                </h3>
                <p className="mt-3 sm:mt-4 text-sm sm:text-lg text-white/75 font-medium max-w-xl leading-snug">
                  See which of your seven systems may be failing you. It takes about 90 seconds.
                </p>
                <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full sm:w-auto">
                  <button
                    onClick={takeMatrixFromVideo}
                    className="w-full sm:w-auto justify-center inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold text-sm sm:text-base px-6 sm:px-8 py-3.5 sm:py-4 hover:brightness-110 active:scale-[0.98] transition focus-ring shadow-[0_12px_40px_-8px_rgba(212,175,55,0.6)]"
                  >
                    Take the Matrix (Free) <ArrowRight size={18} />
                  </button>
                  <button
                    onClick={replay}
                    className="inline-flex items-center gap-2 text-white/70 hover:text-white font-semibold transition-colors focus-ring rounded-md px-3 py-2 text-sm sm:text-base"
                  >
                    <RotateCcw size={16} /> Watch again
                  </button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default VideoSection;
